import React, { useState, useEffect, memo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@/components/modal';
import { Button } from '@/components/button/Buttons';
import { IRole } from '@/types/roles-permissions';

type RoleData = {
  id: string;
  name: string;
  has_admin_panel_access: boolean;
  has_user_front_access: boolean;
  description: string;
};

interface EditRoleModalProps {
  isOpen: boolean;
  isUpdating: boolean;
  role: IRole | null;
  onClose: () => void;
  onUpdateRole: (
    roleId: string,
    roleData: {
      name: string;
      has_admin_panel_access: boolean;
      has_user_front_access: boolean;
      description: string;
    }
  ) => void;
}

const initialRoleData: RoleData = {
  id: '',
  name: '',
  has_admin_panel_access: true,
  has_user_front_access: false,
  description: '',
};

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  isOpen,
  isUpdating,
  role,
  onClose,
  onUpdateRole,
}) => {
  const [roleData, setRoleData] = useState<RoleData>(initialRoleData);
  const [formErrors, setFormErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (role) {
      setRoleData({
        id: role.id,
        name: role.name,
        has_admin_panel_access:
          role.has_admin_panel_access !== undefined
            ? role.has_admin_panel_access
            : true,
        has_user_front_access:
          role.has_user_front_access !== undefined
            ? role.has_user_front_access
            : false,
        description: role.description || '',
      });
      setFormErrors({});
    } else if (!isOpen) {
      setRoleData(initialRoleData);
      setFormErrors({});
    }
  }, [role, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    const fieldName =
      id === 'editRoleName'
        ? 'name'
        : id === 'editDescription'
          ? 'description'
          : id;

    setRoleData((prev) => ({ ...prev, [fieldName]: value }));

    if (id === 'editRoleName' && formErrors.name) {
      setFormErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    const fieldName =
      id === 'editAdminPanelAccess'
        ? 'has_admin_panel_access'
        : 'has_user_front_access';
    setRoleData((prev) => ({ ...prev, [fieldName]: checked }));
  };

  const validateForm = (): boolean => {
    const errors: { name?: string } = {};

    if (!roleData.name.trim()) {
      errors.name = 'Role name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (validateForm() && roleData.id) {
      onUpdateRole(roleData.id, {
        name: roleData.name,
        has_admin_panel_access: roleData.has_admin_panel_access,
        has_user_front_access: roleData.has_user_front_access,
        description: roleData.description,
      });
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="edit-role-modal-title"
    >
      <ModalContent className="max-w-md mx-auto mt-20">
        <ModalHeader>
          <ModalTitle id="edit-role-modal-title">Edit Role</ModalTitle>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="editRoleName"
                  className="block text-sm font-medium text-gray-700 dark:text-white/80 mb-1"
                >
                  Role Name *
                </label>
                <input
                  type="text"
                  id="editRoleName"
                  value={roleData.name}
                  onChange={handleInputChange}
                  className={`w-full input px-3 py-2 border ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-coal-100'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Enter role name"
                  required
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="editAdminPanelAccess"
                    checked={roleData.has_admin_panel_access}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-coal-600 border-gray-300 dark:border-coal-100 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="editAdminPanelAccess"
                    className="ml-2 text-sm font-medium text-gray-700 dark:text-white/80 cursor-pointer"
                  >
                    Admin Panel Access
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="editUserFrontAccess"
                    checked={roleData.has_user_front_access}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-coal-600 border-gray-300 dark:border-coal-100 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="editUserFrontAccess"
                    className="ml-2 text-sm font-medium text-gray-700 dark:text-white/80 cursor-pointer"
                  >
                    User Front Access
                  </label>
                </div>
              </div>
              <div>
                <label
                  htmlFor="editDescription"
                  className="block text-sm font-medium text-gray-700 dark:text-white/80 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="editDescription"
                  value={roleData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full textarea px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter role description"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isUpdating || !roleData.name.trim()}
              >
                {isUpdating ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(EditRoleModal);
